/**
 * apiFetch — fetch wrapper with retry + per-attempt timeout for the
 * Quality Domination HTTP API.
 *
 * The deployed Cloudflare Worker can show three kinds of transient
 * failure to first-time visitors:
 *
 *   - Cold start (~1s): the Worker JIT spins up after idle eviction
 *   - TLS edge hiccup (<500ms): random Cloudflare colo handshake retry
 *   - Cross-region DNS settling: usually <300ms after deploy
 *
 * All three resolve within ~1.5 seconds. A single failed fetch surfaces
 * "Couldn't reach the server" to the user, which causes onboarding
 * churn — they don't know to retry. So we retry automatically (3
 * attempts, exponential backoff capped at 2s) and only surface an
 * error to the UI when ALL attempts have failed.
 *
 * Use `prewarmApi()` on routes immediately preceding any POST so the
 * Worker is hot when the user actually clicks the button — eliminates
 * the cold-start window from the user-visible critical path.
 */

export interface ApiFetchOptions extends RequestInit {
  /** Total attempts including the first. Default 3. */
  readonly maxAttempts?: number;
  /** Per-attempt timeout in ms. Default 6000. */
  readonly timeoutMs?: number;
}

/**
 * Fetch with built-in retry + timeout.
 *
 * - Network errors (TypeError "Failed to fetch", AbortError) → retry
 * - 5xx responses → retry
 * - 4xx responses → return immediately (terminal; the caller decides
 *   whether to show an error or interpret the body)
 * - 2xx / 3xx → return on the first success
 */
export async function apiFetch(
  input: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const { maxAttempts = 3, timeoutMs = 6000, ...init } = options;
  let lastError: unknown;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const res = await fetch(input, {
        ...init,
        signal: AbortSignal.timeout(timeoutMs),
      });
      // 5xx → server-side blip; retry. 4xx → caller's problem; surface
      // immediately so we don't hammer the server with bad requests.
      if (res.status >= 500 && attempt + 1 < maxAttempts) {
        await delay(backoffMs(attempt));
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      if (attempt + 1 < maxAttempts) {
        await delay(backoffMs(attempt));
        continue;
      }
    }
  }
  throw lastError ?? new Error('apiFetch failed after max retries');
}

/**
 * Fire-and-forget request to the API base URL's health endpoint.
 * Wakes the Cloudflare Worker so the next user-facing call doesn't
 * pay cold-start latency.
 */
export function prewarmApi(baseUrl: string): void {
  if (typeof window === 'undefined') return;
  fetch(`${baseUrl}/api/health`, {
    method: 'GET',
    signal: AbortSignal.timeout(3000),
  }).catch(() => {
    // Best-effort; ignore failures.
  });
}

function backoffMs(attempt: number): number {
  // 200ms, 800ms, then capped at 2s. Sum across 3 attempts is ~1s of
  // backoff in the worst case before the 3rd attempt fires — fast
  // enough that the user perceives it as a single, slightly-slow
  // request rather than a stutter.
  return Math.min(200 * 4 ** attempt, 2000);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
